/**
 * Join the Draw Functionality
 * Handles adding/removing a $2 draw product to the cart
 */

class DrawFunctionality {
  constructor() {
    // Your actual draw product variant ID from Shopify
    this.drawProductVariantId = "42674130059330"; // Join the Draw product variant
    this.drawProductPrice = 200; // $2 in cents
    this.checkboxSelector = ".join-draw-checkbox";
    this.isProcessingCheckboxChange = false; // Flag to prevent multiple checkbox changes
    this.lastKnownDrawProductState = false; // Track if draw product is in cart
    console.log(
      "DrawFunctionality initialized with variant ID:",
      this.drawProductVariantId
    );
    this.init();
  }

  init() {
    document.addEventListener("DOMContentLoaded", () => {
      this.bindEvents();
      this.updateCheckboxState();
    });

    // Listen for cart updates
    document.addEventListener("cart:updated", () => {
      this.updateCheckboxState();
    });
  }

  bindEvents() {
    // Handle checkbox changes
    document.addEventListener("change", (event) => {
      if (event.target.matches(this.checkboxSelector)) {
        // Prevent multiple rapid clicks/changes
        if (this.isProcessingCheckboxChange) {
          console.log("Already processing a checkbox change, ignoring");
          event.preventDefault();
          event.stopPropagation();
          return;
        }

        console.log("Checkbox changed:", event.target.checked);
        this.handleCheckboxChange(event.target.checked, event);
      }
    });

    // Setup MutationObserver to watch for cart HTML changes
    this.setupMutationObserver();

    // Also check if checkboxes exist
    setTimeout(() => {
      const checkboxes = document.querySelectorAll(this.checkboxSelector);
      console.log("Found checkboxes:", checkboxes.length);
    }, 1000);
  }

  setupMutationObserver() {
    // Watch the entire cart drawer for any changes
    const cartDrawer = document.querySelector("#CartDrawer") || document.body;

    const observer = new MutationObserver((mutations) => {
      // Look for any changes that might affect the checkbox
      let checkboxWasReplaced = false;

      mutations.forEach((mutation) => {
        if (mutation.type === "childList") {
          // Check if any nodes were added that contain or are checkboxes
          const addedNodes = Array.from(mutation.addedNodes);
          addedNodes.forEach((node) => {
            if (node.nodeType === 1) {
              // Element node
              // Check if this node is the checkbox or contains it
              if (
                (node.matches && node.matches(this.checkboxSelector)) ||
                (node.querySelector &&
                  node.querySelector(this.checkboxSelector))
              ) {
                checkboxWasReplaced = true;
              }
              // Also check for cart drawer footer which contains the checkbox
              if (
                node.matches &&
                (node.matches(".cart-drawer__footer") ||
                  node.matches(".join-draw-wrapper"))
              ) {
                checkboxWasReplaced = true;
              }
            }
          });
        }
      });

      if (checkboxWasReplaced) {
        console.log(
          "Checkbox or its container was replaced! Restoring state:",
          this.lastKnownDrawProductState
        );

        // IMMEDIATELY set checkbox to last known state (synchronous)
        const checkboxes = document.querySelectorAll(this.checkboxSelector);
        checkboxes.forEach((checkbox) => {
          checkbox.checked = this.lastKnownDrawProductState;
          console.log("Set checkbox to:", this.lastKnownDrawProductState);
        });

        // Then verify against cart after a brief delay
        setTimeout(() => {
          this.updateCheckboxState();
        }, 50);
      }
    });

    observer.observe(cartDrawer, {
      childList: true,
      subtree: true,
      attributes: false, // We don't need to watch attribute changes
    });

    console.log("MutationObserver setup complete, watching cart drawer");
  }

  async handleCheckboxChange(isChecked) {
    // Get the checkbox that was clicked
    const clickedCheckbox = event ? event.target : null;

    // Set a processing flag to avoid double toggling
    this.isProcessingCheckboxChange = true;

    // Show loading indicators
    this.showLoadingState(true);

    // Disable all checkboxes during processing to prevent multiple clicks
    const checkboxes = document.querySelectorAll(this.checkboxSelector);
    checkboxes.forEach((checkbox) => {
      checkbox.disabled = true;
    });

    try {
      // Force the checkbox to the new state immediately
      checkboxes.forEach((checkbox) => {
        checkbox.checked = isChecked;
      });

      // Pre-emptively update the UI with the expected price change
      try {
        const cartResponse = await fetch("/cart.js");
        const cart = await cartResponse.json();

        // Calculate expected new price
        const expectedTotalPrice = isChecked
          ? cart.total_price + this.drawProductPrice // Add $2 (200 cents)
          : cart.total_price - this.drawProductPrice; // Remove $2 (200 cents)

        // Create a temporary cart object with the expected price
        const tempCart = { ...cart, total_price: expectedTotalPrice };

        // Update UI immediately with this expected price
        this.updateCartUIElements(tempCart);
      } catch (error) {
        console.error("Error in pre-update:", error);
      }

      // Actually perform the add/remove operation
      if (isChecked) {
        await this.addDrawProduct();
      } else {
        await this.removeDrawProduct();
      }
    } finally {
      // Always re-enable checkboxes and clear the processing flag
      setTimeout(() => {
        checkboxes.forEach((checkbox) => {
          checkbox.disabled = false;
        });
        this.isProcessingCheckboxChange = false;

        // Hide loading state
        this.showLoadingState(false);
      }, 500);
    }
  }

  async addDrawProduct() {
    try {
      // Check if draw product is already in cart
      const cartResponse = await fetch("/cart.js");
      const cart = await cartResponse.json();

      // Find the draw product in cart
      const drawItem = cart.items.find(
        (item) =>
          item.variant_id == this.drawProductVariantId &&
          item.properties &&
          item.properties._draw_entry === "true"
      );

      // If product is already in cart, check if quantity is 1, if not force it to 1
      if (drawItem) {
        console.log("Draw product already in cart");

        // If quantity is more than 1, update it to 1
        if (drawItem.quantity > 1) {
          console.log("Enforcing quantity of 1 for draw product");

          const updateResponse = await fetch("/cart/change.js", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Requested-With": "XMLHttpRequest",
            },
            body: JSON.stringify({
              id: drawItem.key,
              quantity: 1,
            }),
          });

          if (updateResponse.ok) {
            const result = await updateResponse.json();
            this.updateCartWithoutRefresh(result);
          }
        }

        this.updateCheckboxState(); // Make sure checkbox remains checked
        return; // Exit early
      }

      console.log(
        "Adding draw product with variant ID:",
        this.drawProductVariantId
      );

      // Add the draw product with quantity 1
      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify({
          id: this.drawProductVariantId,
          quantity: 1,
          properties: {
            _draw_entry: "true", // Hidden property to identify this item
          },
        }),
      });

      if (response.ok) {
        console.log("Successfully added draw product");
        const result = await response.json();
        console.log("Add result:", result);
        this.updateCartWithoutRefresh(result);

        // Show confirmation

      } else {
        const errorData = await response.json();
        console.error("Failed to add draw product:", errorData);
        this.revertCheckbox(false);
      }
    } catch (error) {
      console.error("Error adding draw product:", error);
      this.revertCheckbox(false);
    }
  }

  async removeDrawProduct() {
    try {
      console.log("Attempting to remove draw product");
      // Get current cart
      const cartResponse = await fetch("/cart.js");
      const cart = await cartResponse.json();

      // Find the draw product in cart
      const drawItem = cart.items.find(
        (item) =>
          item.variant_id == this.drawProductVariantId &&
          item.properties &&
          item.properties._draw_entry === "true"
      );

      console.log("Found draw item:", drawItem);

      if (drawItem) {
        console.log("Removing draw item with key:", drawItem.key);
        // Remove the item by setting quantity to 0
        const response = await fetch("/cart/change.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
          body: JSON.stringify({
            id: drawItem.key,
            quantity: 0,
          }),
        });

        if (response.ok) {
          console.log("Successfully removed draw product");
          const result = await response.json();
          this.updateCartWithoutRefresh(result);
        } else {
          console.error("Failed to remove draw product");
          this.revertCheckbox(true);
        }
      } else {
        console.log("Draw product not found in cart");
      }
    } catch (error) {
      console.error("Error removing draw product:", error);
      this.revertCheckbox(true);
    }
  }

  showLoadingState(show) {
    const loaderId = "draw-functionality-loader";

    if (show) {
      // Create loader if it doesn't exist
      if (!document.getElementById(loaderId)) {
        const loader = document.createElement("div");
        loader.id = loaderId;
        loader.innerHTML = `
          <div class="draw-loader-overlay">
            <div class="draw-loader-spinner">
              <div class="draw-spinner"></div>
              <p>Updating cart...</p>
            </div>
          </div>
        `;
        loader.style.cssText = `
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 10000;
        `;

        // Add styles for the loader
        const style = document.createElement("style");
        style.textContent = `
          .draw-loader-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(0, 0, 0, 0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 10000;
            animation: fadeIn 0.2s ease-in;
          }
          
          .draw-loader-spinner {
            background: white;
            padding: 30px 40px;
            border-radius: 12px;
            text-align: center;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
          }
          
          .draw-spinner {
            width: 50px;
            height: 50px;
            margin: 0 auto 15px;
            border: 4px solid #f3f3f3;
            border-top: 4px solid #3498db;
            border-radius: 50%;
            animation: spin 1s linear infinite;
          }
          
          .draw-loader-spinner p {
            margin: 0;
            font-size: 16px;
            color: #333;
            font-weight: 500;
          }
          
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
        `;

        document.body.appendChild(loader);
        document.head.appendChild(style);
      }
    } else {
      // Remove loader
      const loader = document.getElementById(loaderId);
      if (loader) {
        loader.style.opacity = "0";
        loader.style.transition = "opacity 0.2s ease-out";
        setTimeout(() => {
          if (loader.parentNode) {
            loader.parentNode.removeChild(loader);
          }
        }, 200);
      }
    }
  }

  showNotification(message) {
    // Create a notification element
    const notification = document.createElement("div");
    notification.className = "draw-notification";
    notification.textContent = message;
    notification.style.cssText = `
      position: fixed;
      bottom: 20px;
      left: 50%;
      transform: translateX(-50%);
      background-color: #333;
      color: white;
      padding: 12px 20px;
      border-radius: 4px;
      z-index: 9999;
      font-size: 16px;
    `;

    // Add to page
    document.body.appendChild(notification);

    // Remove after 3 seconds
    setTimeout(() => {
      notification.style.opacity = "0";
      notification.style.transition = "opacity 0.5s";
      setTimeout(() => {
        document.body.removeChild(notification);
      }, 500);
    }, 3000);
  }

  async updateCheckboxState() {
    try {
      // Get current cart
      const cartResponse = await fetch("/cart.js");
      const cart = await cartResponse.json();

      // Check if draw product is in cart
      const hasDrawProduct = cart.items.some(
        (item) =>
          item.variant_id == this.drawProductVariantId &&
          item.properties &&
          item.properties._draw_entry === "true"
      );

      // Check if draw product is the ONLY item with a price (ignore free gifts)
      if (hasDrawProduct) {
        // Get all items that have a price (not free gifts)
        const paidItems = cart.items.filter((item) => item.price > 0);

        // If draw product is the only paid item, remove it
        const isDrawOnlyPaidItem =
          paidItems.length === 1 &&
          paidItems[0].variant_id == this.drawProductVariantId &&
          paidItems[0].properties &&
          paidItems[0].properties._draw_entry === "true";

        if (isDrawOnlyPaidItem) {
          console.log(
            "Draw product is the only paid item in cart (ignoring free gifts) - removing it"
          );
          
          // Show loader
          this.showLoadingState(true);
          
          // Remove the draw product
          await this.removeDrawProduct();
          
          // After removing, update the state
          this.lastKnownDrawProductState = false;
          const checkboxes = document.querySelectorAll(this.checkboxSelector);
          checkboxes.forEach((checkbox) => {
            checkbox.checked = false;
          });
          
          // Refresh the cart to show empty state
          await this.refreshCartToEmptyState();
          
          // Hide loader
          this.showLoadingState(false);
          
          return; // Exit early
        }
      }

      // Store the state
      this.lastKnownDrawProductState = hasDrawProduct;
      console.log("Updated lastKnownDrawProductState to:", hasDrawProduct);

      // Update all checkboxes
      const checkboxes = document.querySelectorAll(this.checkboxSelector);
      checkboxes.forEach((checkbox) => {
        checkbox.checked = hasDrawProduct;
      });
    } catch (error) {
      console.error("Error updating checkbox state:", error);
    }
  }

  revertCheckbox(state) {
    const checkboxes = document.querySelectorAll(this.checkboxSelector);
    checkboxes.forEach((checkbox) => {
      checkbox.checked = state;
    });
  }

  async refreshCartToEmptyState() {
    try {
      console.log("Refreshing cart to show empty state");
      
      // Fetch fresh cart sections to show empty cart
      const sectionsResponse = await fetch(
        "/?sections=cart-items,main-cart-items,cart-icon-bubble,cart-live-region-text,cart-drawer"
      );
      const sections = await sectionsResponse.json();

      // Update cart drawer to show empty state
      if (sections["cart-drawer"]) {
        const cartDrawerElement = document.querySelector("cart-drawer");
        if (cartDrawerElement) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(
            sections["cart-drawer"],
            "text/html"
          );
          
          // Get the entire drawer inner content
          const newDrawerInner = doc.querySelector(".drawer__inner");
          const currentDrawerInner = document.querySelector(".drawer__inner");
          
          if (newDrawerInner && currentDrawerInner) {
            currentDrawerInner.innerHTML = newDrawerInner.innerHTML;
          }
          
          // Add 'is-empty' class to cart drawer
          cartDrawerElement.classList.add("is-empty");
        }
      }

      // Update cart count to 0
      const cartCountElements = document.querySelectorAll(
        ".wbhcartitem, .cart-count-bubble, [data-cart-count]"
      );
      cartCountElements.forEach((el) => {
        el.textContent = "0";
      });

      // Dispatch cart updated event
      document.dispatchEvent(
        new CustomEvent("cart:updated", {
          detail: { cart: { item_count: 0, items: [], total_price: 0 } },
        })
      );

      console.log("Cart refreshed to empty state");
    } catch (error) {
      console.error("Error refreshing cart to empty state:", error);
    }
  }

  updateCart() {
    // Trigger cart update events that other parts of the theme listen to
    fetch("/cart.js")
      .then((response) => response.json())
      .then((cart) => {
        // Update cart UI elements
        this.updateCartUI(cart);

        // Dispatch custom event
        document.dispatchEvent(
          new CustomEvent("cart:updated", {
            detail: { cart },
          })
        );
      });
  }

  updateCartWithoutRefresh(cartData) {
    console.log("Updating cart without refresh, data:", cartData);

    // First update the UI with the data we have
    if (cartData && cartData.items && cartData.total_price) {
      this.updateCartUIElements(cartData);
    }

    // But always fetch the latest cart to ensure we have the most up-to-date data
    fetch("/cart.js")
      .then((response) => response.json())
      .then((cart) => {
        console.log("Fetched latest cart data:", cart);
        this.updateCartUIElements(cart);

        // Also fetch section content to update cart HTML
        this.fetchCartSections();
      })
      .catch((error) => {
        console.error("Error fetching cart:", error);
      });
  }

  async fetchCartSections() {
    try {
      // FIRST: Check if draw product is in cart BEFORE updating HTML
      const cartResponse = await fetch("/cart.js");
      const cart = await cartResponse.json();
      const hasDrawProduct = cart.items.some(
        (item) =>
          item.variant_id == this.drawProductVariantId &&
          item.properties &&
          item.properties._draw_entry === "true"
      );
      console.log("Draw product in cart before HTML update:", hasDrawProduct);

      // THEN: Fetch and update cart sections HTML
      const sectionsResponse = await fetch(
        "/?sections=cart-items,main-cart-items,cart-icon-bubble,cart-live-region-text,cart-drawer"
      );
      const sections = await sectionsResponse.json();
      console.log("Got cart sections:", Object.keys(sections));

      // Update cart drawer if it exists
      if (sections["cart-drawer"]) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(
          sections["cart-drawer"],
          "text/html"
        );

        // Update cart items
        const newDrawerItems = doc.querySelector("#CartDrawer-CartItems");
        const currentDrawerItems = document.querySelector(
          "#CartDrawer-CartItems"
        );
        if (newDrawerItems && currentDrawerItems) {
          currentDrawerItems.innerHTML = newDrawerItems.innerHTML;
        }

        // Update totals
        const newTotal = doc.querySelector(".cart-drawer__footer .totals");
        const currentTotal = document.querySelector(
          ".cart-drawer__footer .totals"
        );
        if (newTotal && currentTotal) {
          currentTotal.innerHTML = newTotal.innerHTML;
        }

        // Update the footer section which contains the checkbox
        const newFooter = doc.querySelector(".cart-drawer__footer");
        const currentFooter = document.querySelector(".cart-drawer__footer");
        if (newFooter && currentFooter) {
          // Before replacing, get the checkbox from new HTML and set its state
          const checkboxInNewFooter = newFooter.querySelector(
            this.checkboxSelector
          );
          if (checkboxInNewFooter) {
            checkboxInNewFooter.checked = hasDrawProduct;
            console.log("Setting checkbox in new HTML to:", hasDrawProduct);
          }
          currentFooter.innerHTML = newFooter.innerHTML;
        }
      }

      // Update main cart if it exists
      if (sections["main-cart-items"]) {
        const mainCartItems = document.getElementById("main-cart-items");
        if (mainCartItems) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(
            sections["main-cart-items"],
            "text/html"
          );
          const newItems = doc.getElementById("main-cart-items");
          if (newItems) {
            mainCartItems.innerHTML = newItems.innerHTML;
          }
        }
      }

      // IMMEDIATELY: Force checkbox state after DOM is updated
      const checkboxes = document.querySelectorAll(this.checkboxSelector);
      checkboxes.forEach((checkbox) => {
        checkbox.checked = hasDrawProduct;
      });
      console.log("Immediately set checkbox state to:", hasDrawProduct);

      // This triggers any JS in the cart to reinitialize
      document.dispatchEvent(new CustomEvent("cart:refresh"));

      // FINALLY: One more check with a small delay to ensure it sticks
      setTimeout(() => {
        const finalCheckboxes = document.querySelectorAll(
          this.checkboxSelector
        );
        finalCheckboxes.forEach((checkbox) => {
          checkbox.checked = hasDrawProduct;
        });
        console.log("Final checkbox state set to:", hasDrawProduct);
      }, 100);
    } catch (error) {
      console.error("Error updating cart sections:", error);
    }
  }

  updateCartUIElements(cart) {
    console.log("Updating cart UI with total:", cart.total_price);

    try {
      // ONLY update cart totals and subtotals, NOT individual product prices
      // This prevents the flickering of individual product prices

      // Update subtotal price
      const subtotalPriceElements = document.querySelectorAll(
        ".totals__subtotal + .product-option"
      );
      if (subtotalPriceElements?.length) {
        subtotalPriceElements.forEach((el) => {
          const formattedPrice = this.formatMoney(cart.total_price);
          el.innerHTML = formattedPrice;
        });
      }

      // Update cart count display (in multiple locations)
      const cartCountElements = document.querySelectorAll(
        ".wbhcartitem, .cart-count-bubble, [data-cart-count]"
      );
      if (cartCountElements?.length) {
        cartCountElements.forEach((el) => {
          el.textContent = cart.item_count;
        });
      }

      // Update cart subtotal text
      const subtotalTextElements =
        document.querySelectorAll(".totals__subtotal");
      if (subtotalTextElements?.length) {
        subtotalTextElements.forEach((el) => {
          if (el.textContent.includes("Subtotal")) {
            el.textContent = `Subtotal (${cart.item_count})`;
          }
        });
      }

      // Update cart drawer heading
      const drawerHeadingElements =
        document.querySelectorAll(".drawer__heading");
      if (drawerHeadingElements?.length) {
        drawerHeadingElements.forEach((el) => {
          if (el.textContent.includes("Cart")) {
            el.textContent = `Cart (${cart.item_count})`;
          }
        });
      }

      // Update any elements showing total price
      const cartTotalElements = document.querySelectorAll(
        ".cart__checkout-button .price, .totals__subtotal-value, .cart__ctas .price, [data-cart-total], .totals .price-original, .cart-drawer__footer .product-option.price-original, .cart__footer .product-option.price-original"
      );
      if (cartTotalElements?.length) {
        const formattedPrice = this.formatMoney(cart.total_price);
        cartTotalElements.forEach((el) => {
          // Make sure we're not inside a product row (avoid changing product prices)
          if (!el.closest(".cart-item") && !el.closest('tr[id*="CartItem"]')) {
            el.innerHTML = formattedPrice;
          }
        });
      }

      // Hide quantity controls for draw product
      setTimeout(() => {
        // Direct approach to hide quantity controls for our product ID
        const drawInputs = document.querySelectorAll(
          'input[data-quantity-variant-id="42674130059330"]'
        );
        drawInputs.forEach((input) => {
          // Hide the entire quantity control
          const quantityParent =
            input.closest(".quantity") ||
            input.closest("quantity-input") ||
            input.closest(".cart-quantity");
          if (quantityParent) {
            quantityParent.style.display = "none";
          }

          // Hide the input itself
          input.style.display = "none";
          input.style.visibility = "hidden";

          // Hide all buttons next to this input
          const prevButton = input.previousElementSibling;
          const nextButton = input.nextElementSibling;
          if (prevButton && prevButton.tagName === "BUTTON") {
            prevButton.style.display = "none";
          }
          if (nextButton && nextButton.tagName === "BUTTON") {
            nextButton.style.display = "none";
          }
        });

        // Also try to hide by aria-label
        const drawLabelInputs = document.querySelectorAll(
          'input[aria-label*="Join the Draw Entry"]'
        );
        drawLabelInputs.forEach((input) => {
          const quantityParent =
            input.closest(".quantity") ||
            input.closest("quantity-input") ||
            input.closest(".cart-quantity");
          if (quantityParent) {
            quantityParent.style.display = "none";
          }

          // Hide the input itself and surrounding buttons
          input.style.display = "none";
          input.style.visibility = "hidden";

          const prevButton = input.previousElementSibling;
          const nextButton = input.nextElementSibling;
          if (prevButton && prevButton.tagName === "BUTTON") {
            prevButton.style.display = "none";
          }
          if (nextButton && nextButton.tagName === "BUTTON") {
            nextButton.style.display = "none";
          }
        });
      }, 300);
    } catch (err) {
      console.error("Error updating cart UI elements:", err);
    }

    // Dispatch custom event
    document.dispatchEvent(
      new CustomEvent("cart:updated", {
        detail: { cart },
      })
    );
  }

  updateCartUI(cart) {
    // Update cart count
    const cartCountElements = document.querySelectorAll(
      "[data-cart-count], .wbhcartitem"
    );
    cartCountElements.forEach((element) => {
      element.textContent = cart.item_count;
    });

    // Update cart total
    const cartTotalElements = document.querySelectorAll("[data-cart-total]");
    cartTotalElements.forEach((element) => {
      element.innerHTML = this.formatMoney(cart.total_price);
    });

    // Update specific cart sections if they exist
    const cartItemsSection = document.querySelector("#main-cart-items");
    const cartDrawerItems = document.querySelector("#CartDrawer-CartItems");

    if (cartItemsSection) {
      // Reload main cart
      this.reloadCartSection("main-cart-items");
    }

    if (cartDrawerItems) {
      // Reload cart drawer
      this.reloadCartDrawer();
    }
  }

  async reloadCartSection(sectionId) {
    try {
      const response = await fetch(`/?section_id=${sectionId}`);
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");
      const newSection = doc.querySelector(`[data-id="${sectionId}"]`);
      const currentSection = document.querySelector(`[data-id="${sectionId}"]`);

      if (newSection && currentSection) {
        currentSection.innerHTML = newSection.innerHTML;
      }
    } catch (error) {
      console.error("Error reloading cart section:", error);
      // Fallback: reload the page
      window.location.reload();
    }
  }

  async reloadCartDrawer() {
    try {
      const response = await fetch("/?section_id=cart-drawer");
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");
      const newDrawerItems = doc.querySelector("#CartDrawer-CartItems");
      const currentDrawerItems = document.querySelector(
        "#CartDrawer-CartItems"
      );

      if (newDrawerItems && currentDrawerItems) {
        currentDrawerItems.innerHTML = newDrawerItems.innerHTML;
      }

      // Update drawer totals
      const newDrawerFooter = doc.querySelector(".cart-drawer__footer .totals");
      const currentDrawerFooter = document.querySelector(
        ".cart-drawer__footer .totals"
      );

      if (newDrawerFooter && currentDrawerFooter) {
        currentDrawerFooter.innerHTML = newDrawerFooter.innerHTML;
      }

      // After updating the HTML, ensure checkbox state is correct
      this.updateCheckboxState();
    } catch (error) {
      console.error("Error reloading cart drawer:", error);
    }
  }

  formatMoney(cents) {
    // Basic money formatting - adjust based on your currency settings
    const amount = cents / 100;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  }
}

// Initialize the draw functionality
new DrawFunctionality();
